import { useMemo } from "react";
import type { AccountFeature } from "@/types/model/User";
import { useUsersStore } from "@/zustand/usersStore";
import { userIsSelfService } from "@/utilities/planHelpers";
import {
  featureAccessResolver,
  FT_NAME,
} from "@/components/routes/feature-access/featureAccessResolver";
import type { FeatureAccessConfig } from "@/components/routes/feature-access/featureAccess.types";

const useAccountFeatures = () => {
  const user = useUsersStore((state) => state.user);
  const { accountFeatures, planType = "", planMode = "" } = user;

  const activeModules = useMemo(
    () => accountFeatures?.map((el: AccountFeature) => el.name) ?? [],
    [accountFeatures]
  );

  // Get feature access configuration for a view
  const getFeatureAccess = (viewName: string): FeatureAccessConfig => {
    return featureAccessResolver.resolveAccess(viewName, {
      accountFeatures,
      planType: planType || undefined,
      planMode: (planMode || undefined) as
        | "demo"
        | "free_trial"
        | "selfservice"
        | "managed"
        | "appsumo",
    });
  };

  // Check if feature is visible (backward compatibility)
  const isFeatureVisible = (viewName: string): boolean => {
    const access = getFeatureAccess(viewName);
    return access.visibility === "visible" || access.visibility === "locked";
  };

  //  Check if should show GetFeature component
  const showGetFeature = (viewName: string = ""): boolean => {
    // Selfservice plans don't show GetFeature
    if (userIsSelfService(user)) return false;

    // For other plans, check if feature is locked
    const access = getFeatureAccess(viewName);
    return access.showGetFeature;
  };

  // Can user see channel section in menu
  const canUserSeeChannelSection = [
    FT_NAME.META,
    FT_NAME.MICROSOFT,
    FT_NAME.TABOOLA,
    FT_NAME.HUBSPOT,
    FT_NAME.TIKTOK,
    FT_NAME.GOOGLE,
  ].some((featureName) => {
    const access = getFeatureAccess(featureName);
    return access.visibility === "visible" || access.visibility === "locked";
  });

  return {
    getFeatureAccess,
    isFeatureVisible,
    showGetFeature,
    activeModules,
    canUserSeeChannelSection,
  };
};

export default useAccountFeatures;
